import { useEffect, useRef, useState } from "react";
import { AiOutlineSend } from "react-icons/ai";
import { LuSquare } from "react-icons/lu";
import MarkdownRenderer from "../../markdown/MarkdownRenderer";

const COLLAPSE_THRESHOLD_CHARS = 600;
const COLLAPSE_THRESHOLD_LINES = 8;

const shouldCollapse = (text) => {
    if (!text) return false;
    if (text.length > COLLAPSE_THRESHOLD_CHARS) return true;
    return (text.match(/\n/g) || []).length > COLLAPSE_THRESHOLD_LINES;
};

const truncate = (text) => {
    if (!text) return "";
    const lines = text.split("\n");
    const byLines = lines.length > COLLAPSE_THRESHOLD_LINES
        ? lines.slice(0, COLLAPSE_THRESHOLD_LINES).join("\n")
        : text;
    if (byLines.length > COLLAPSE_THRESHOLD_CHARS) {
        return `${byLines.slice(0, COLLAPSE_THRESHOLD_CHARS).trimEnd()}…`;
    }
    return `${byLines.trimEnd()}…`;
};

const bubbleBase = {
    maxWidth: "90%",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.5rem",
    fontSize: "0.875rem",
    lineHeight: 1.4,
    wordBreak: "break-word",
};

const msgRoleStyle = (role) => {
    if (role === "user") return { display: "flex", justifyContent: "flex-end" };
    if (role === "system") return { display: "flex", justifyContent: "center" };
    return { display: "flex", justifyContent: "flex-start" };
};

const bubbleRoleStyle = (role) => {
    if (role === "user") {
        return { ...bubbleBase, background: "var(--lightBlue)", color: "var(--white)" };
    }
    if (role === "system") {
        return {
            ...bubbleBase,
            background: "var(--lightestGrey)",
            color: "var(--darkGrey)",
            fontSize: "0.8rem",
        };
    }
    return { ...bubbleBase, background: "var(--lightestGrey)", color: "var(--black)" };
};

const ToolBubble = ({ isCall, toolName, content }) => {
    const collapsible = shouldCollapse(content);
    const [expanded, setExpanded] = useState(false);
    const displayContent = collapsible && !expanded ? truncate(content) : content;

    return (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{
                ...bubbleBase,
                maxWidth: "95%",
                background: "var(--white)",
                border: "1px solid var(--lighterGrey)",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: "0.75rem",
            }}>
                <div style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "var(--darkGrey)",
                    marginBottom: "0.35rem",
                }}>
                    {isCall ? "Calling" : "Tool"} · {toolName || "result"}
                </div>
                <div style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    ...(collapsible && !expanded ? { maxHeight: "8rem", overflow: "hidden" } : {}),
                }}>
                    {displayContent}
                </div>
                {collapsible && (
                    <button
                        type="button"
                        style={{
                            marginTop: "0.35rem",
                            padding: 0,
                            border: "none",
                            background: "none",
                            color: "var(--lightBlue)",
                            fontSize: "0.75rem",
                            cursor: "pointer",
                        }}
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded ? "Show less" : "Show more"}
                    </button>
                )}
            </div>
        </div>
    );
};

const ChatMessage = ({ msg }) => {
    if (msg.role === "tool_call") {
        return <ToolBubble isCall toolName={msg.toolName} content={msg.text} />;
    }
    if (msg.role === "tool") {
        return <ToolBubble isCall={false} toolName={msg.toolName} content={msg.text} />;
    }

    return (
        <div style={msgRoleStyle(msg.role)}>
            <div style={bubbleRoleStyle(msg.role)}>
                <MarkdownRenderer>{msg.text ?? ""}</MarkdownRenderer>
            </div>
        </div>
    );
};

const VendorInvoiceChat = ({
    disabled,
    loading,
    messages,
    onSend,
    onStop,
    fill = false,
    title = "Agent conversation",
    subtitle = "Send corrections or guidance to refine the payables draft.",
    emptyHint = "Review the payables and send feedback to the agent if fields need correction.",
    placeholder = "Send feedback to the agent…",
}) => {
    const [textInput, setTextInput] = useState("");
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = textInput.trim();
        if (!trimmed || disabled || loading) return;
        onSend(trimmed);
        setTextInput("");
    };

    const sendDisabled = disabled || loading || !textInput.trim();

    return (
        <div style={{
            border: "1px solid var(--lighterGrey)",
            borderRadius: "0.5rem",
            display: "flex",
            flexDirection: "column",
            height: fill ? "100%" : 520,
            minHeight: fill ? 0 : undefined,
            background: "var(--white)",
            overflow: "hidden",
        }}>
            <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--lighterGrey)", flexShrink: 0 }}>
                <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: 0 }}>{title}</h3>
                <p style={{ fontSize: "0.8rem", color: "var(--darkGrey)", marginTop: "0.25rem", marginBottom: 0 }}>
                    {subtitle}
                </p>
            </div>
            <div style={{
                flex: 1,
                overflowY: "auto",
                padding: "0.75rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                scrollbarWidth: "thin",
                minHeight: 0,
            }}>
                {messages.length === 0 && !loading && (
                    <p style={{ fontSize: "0.85rem", color: "var(--darkGrey)", margin: 0 }}>
                        {emptyHint}
                    </p>
                )}
                {messages.map((msg) => (
                    <ChatMessage key={msg.id} msg={msg} />
                ))}
                {loading && !messages.some((m) => m.streaming) && (
                    <div style={msgRoleStyle("system")}>
                        <div style={bubbleRoleStyle("system")}>
                            Agent is processing…
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <form
                style={{
                    display: "flex",
                    gap: "0.5rem",
                    padding: "0.75rem",
                    borderTop: "1px solid var(--lighterGrey)",
                    flexShrink: 0,
                }}
                onSubmit={handleSubmit}
            >
                <input
                    type="text"
                    style={{
                        flex: 1,
                        border: "1px solid var(--lighterGrey)",
                        borderRadius: "0.35rem",
                        padding: "0.5rem 0.75rem",
                        fontSize: "0.875rem",
                        background: (disabled || loading) ? "var(--lightestGrey)" : undefined,
                    }}
                    placeholder={disabled ? "Waiting for agent…" : placeholder}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    disabled={disabled || loading}
                />
                {loading && onStop ? (
                    <button
                        type="button"
                        style={{
                            border: "none",
                            background: "var(--lightBlue)",
                            color: "var(--white)",
                            borderRadius: "0.35rem",
                            width: "2.25rem",
                            display: "grid",
                            placeItems: "center",
                            cursor: "pointer",
                            opacity: 1,
                        }}
                        onClick={onStop}
                        aria-label="Stop generation"
                    >
                        <LuSquare size="0.75rem" fill="currentColor" />
                    </button>
                ) : (
                    <button
                        type="submit"
                        style={{
                            border: "none",
                            background: "var(--lightBlue)",
                            color: "var(--white)",
                            borderRadius: "0.35rem",
                            width: "2.25rem",
                            display: "grid",
                            placeItems: "center",
                            cursor: sendDisabled ? "not-allowed" : "pointer",
                            opacity: sendDisabled ? 0.5 : 1,
                        }}
                        disabled={sendDisabled}
                        aria-label="Send message"
                    >
                        <AiOutlineSend />
                    </button>
                )}
            </form>
        </div>
    );
};

export default VendorInvoiceChat;
